'use client';

import { useUserContext } from '@/context/user';
import { useToasts } from '@/hooks/Toasts';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useMutation, useQuery } from '@apollo/client';
import clsx from 'clsx';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import { AlertCircle, Check } from 'react-feather';
import couponPointPng from '../../assets/images/rewards-program/coupon-point.png';
import copyPng from '../../assets/images/vip/copy.png';
import { GET_CUSTOMER_REWARDS_BALANCE, REDEEM_COUPON_CODE } from './gql';
import RedeemModal from './redeemModal';
import SignTip from './signTip';

const initRedeemInfoState = () => ({
  open: false,
  type: '',
  description: '',
});

const RedeemPoints = props => {
  const [{ isSignedIn }] = useUserContext();
  const router = useRouter();
  const [, { addToast }] = useToasts();
  const [loadingMap, setLoadingMap] = useState({
    orderOver10off: false,
    orderOver15off: false,
    memberAll15off: false,
    memberK2K320off: false,
    memberK325off: false,
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [redeemModalInfo, setRedeemModalInfo] = useState(initRedeemInfoState);
  const { isMobile } = useIsMobile();
  const confirmResolveRef = useRef();

  const [reqRedeemCouponCode] = useMutation(REDEEM_COUPON_CODE, {
    errorPolicy: 'all',
    refetchQueries: [
      {
        query: GET_CUSTOMER_REWARDS_BALANCE,
      },
    ],
  });
  const { data: rewardsBalanceData } = useQuery(GET_CUSTOMER_REWARDS_BALANCE, {
    fetchPolicy: 'cache-first',
  });

  const confirmToRedeem = async () => {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve;
      setShowConfirmModal(true);
    });
  };

  const handleCopy = couponCode => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(couponCode)
        .then(() => {
          addToast({
            type: 'success',
            icon: <Check />,
            message: 'Code copied to clipboard!',
          });
        })
        .catch(err => {
          addToast({
            type: 'error',
            icon: <AlertCircle />,
            message: 'Failed to copy code!',
          });
        });
    } else {
      addToast({
        type: 'error',
        icon: <AlertCircle />,
        message: 'Failed to copy code!',
      });
    }
  };

  const afterRequest = (response, isRequestOk) => {
    if (isRequestOk) {
      const redeemCouponCode = response.data.redeemCouponCode;

      if (String(redeemCouponCode.code) === '200') {
        setRedeemModalInfo({
          open: true,
          type: 'succeed',
        });
        setCouponCode(redeemCouponCode.content);
      } else {
        setRedeemModalInfo({
          open: true,
          type: 'failed',
          description: redeemCouponCode.content,
        });
      }
    } else {
      const errContent = response.networkError.result?.content || response.message;

      setRedeemModalInfo({
        open: true,
        type: 'failed',
        description: errContent,
      });
    }
  };

  const handleMobileCardClick = cardConfig => {
    if (!isMobile) {
      return;
    }

    if (!isSignedIn) {
      router.push('/sign-in');

      return;
    }

    cardConfig.btnOnClick?.();
  };

  const redeemCouponConfigs = [
    {
      title: '$10 OFF',
      subTitle: 'On Order Over $49',
      pointDesc: '200 Points',
      loading: loadingMap.orderOver10off,
      btnOnClick: async () => {
        if (loadingMap.orderOver10off) {
          return;
        }

        const confirm = await confirmToRedeem();

        if (!confirm) {
          return;
        }

        setLoadingMap(x => ({
          ...x,
          orderOver10off: true,
        }));
        reqRedeemCouponCode({
          variables: {
            points: 200,
            type: 'order_over_10off',
          },
        })
          .then(res => {
            afterRequest(res, true);
            setLoadingMap(x => ({
              ...x,
              orderOver10off: false,
            }));
          })
          .catch(err => {
            afterRequest(err, false);
            setLoadingMap(x => ({
              ...x,
              orderOver10off: false,
            }));
          });
      },
    },
    {
      title: '$15 OFF',
      subTitle: 'On Order Over $99',
      pointDesc: '300 Points',
      loading: loadingMap.orderOver15off,
      btnOnClick: async () => {
        if (loadingMap.orderOver15off) {
          return;
        }

        const confirm = await confirmToRedeem();

        if (!confirm) {
          return;
        }

        setLoadingMap(x => ({
          ...x,
          orderOver15off: true,
        }));
        reqRedeemCouponCode({
          variables: {
            points: 300,
            type: 'order_over_15off',
          },
        })
          .then(res => {
            afterRequest(res, true);
            setLoadingMap(x => ({
              ...x,
              orderOver15off: false,
            }));
          })
          .catch(err => {
            afterRequest(err, false);
            setLoadingMap(x => ({
              ...x,
              orderOver15off: false,
            }));
          });
      },
    },
    {
      title: '15% OFF',
      subTitle: 'For K1,K2,K3',
      pointDesc: '300 Points',
      loading: loadingMap.memberAll15off,
      btnOnClick: async () => {
        if (loadingMap.memberAll15off) {
          return;
        }

        const confirm = await confirmToRedeem();

        if (!confirm) {
          return;
        }

        setLoadingMap(x => ({
          ...x,
          memberAll15off: true,
        }));
        reqRedeemCouponCode({
          variables: {
            points: 300,
            type: 'member_all_15off',
          },
        })
          .then(res => {
            afterRequest(res, true);
            setLoadingMap(x => ({
              ...x,
              memberAll15off: false,
            }));
          })
          .catch(err => {
            afterRequest(err, false);
            setLoadingMap(x => ({
              ...x,
              memberAll15off: false,
            }));
          });
      },
    },
    {
      title: (
        <div>
          <span className='hidden sm_inline'>EXCLUSIVE 20% OFF</span>
          <div className='sm_hidden'>EXCLUSIVE</div>
          <div className='sm_hidden'>20% OFF</div>
        </div>
      ),
      subTitle: 'For K2,K3',
      pointDesc: '400 Points',
      loading: loadingMap.memberK2K320off,
      btnOnClick: async () => {
        if (loadingMap.memberK2K320off) {
          return;
        }

        const confirm = await confirmToRedeem();

        if (!confirm) {
          return;
        }

        setLoadingMap(x => ({
          ...x,
          memberK2K320off: true,
        }));
        reqRedeemCouponCode({
          variables: {
            points: 400,
            type: 'member_k2_k3_20off',
          },
        })
          .then(res => {
            afterRequest(res, true);
            setLoadingMap(x => ({
              ...x,
              memberK2K320off: false,
            }));
          })
          .catch(err => {
            afterRequest(err, false);
            setLoadingMap(x => ({
              ...x,
              memberK2K320off: false,
            }));
          });
      },
    },
    {
      title: (
        <div>
          <span className='hidden sm_inline'>EXCLUSIVE 25% OFF</span>
          <div className='sm_hidden'>EXCLUSIVE</div>
          <div className='sm_hidden'>25% OFF</div>
        </div>
      ),
      subTitle: 'For K3',
      pointDesc: '500 Points',
      loading: loadingMap.memberK325off,
      btnOnClick: async () => {
        if (loadingMap.memberK325off) {
          return;
        }

        const confirm = await confirmToRedeem();

        if (!confirm) {
          return;
        }

        setLoadingMap(x => ({
          ...x,
          memberK325off: true,
        }));
        reqRedeemCouponCode({
          variables: {
            points: 500,
            type: 'member_k3_25off',
          },
        })
          .then(res => {
            afterRequest(res, true);
            setLoadingMap(x => ({
              ...x,
              memberK325off: false,
            }));
          })
          .catch(err => {
            afterRequest(err, false);
            setLoadingMap(x => ({
              ...x,
              memberK325off: false,
            }));
          });
      },
    },
  ];

  return (
    <div className={clsx('font-poppins', props.className)}>
      <h2
        className='text-center text-lg font-bold leading-[22px] text-primary lg_text-[30px] lg_leading-10'
        id='redeem-points'
      >
        REDEEM POINTS
      </h2>

      {isSignedIn ? (
        <div className='mx-auto mt-[5px] flex max-w-[1440px] flex-col justify-between lg_mt-2.5 lg_flex-row'>
          <span className='lg_basis-[33%]' />
          <span className=' text-center text-sm font-normal leading-5  text-info lg_basis-[33%] lg_text-lg lg_leading-[22px]'>
            REMAIN POINTS: {rewardsBalanceData?.rewards.balance}
          </span>
          <Link
            href='/vip'
            className={clsx(
              'mt-2.5 lg_mt-0  lg_basis-[33%] ',
              'text-center font-sans text-sm font-bold leading-[30px] text-[#2F2F2F] lg_text-right lg_text-base lg_leading-5',
              'underline',
            )}
          >
            View My Account
          </Link>
        </div>
      ) : null}

      <div className='mx-auto mt-[30px] max-w-[1440px] bg-[#F6F6F6] py-9 default_mt-[50px] lg_py-[33px]'>
        <div className='text-center text-sm font-normal leading-5 text-primary lg_text-lg lg_leading-[22px]'>
          DEDUCT THE ORDER PRICE
        </div>
        <p className='mt-[30px] text-center text-sm font-normal leading-5 text-primary lg_mt-[50px] lg_text-lg lg_leading-[22px]'>
          50 POINTS = $1
        </p>
        <p className='mt-2.5 text-center font-sans text-sm font-bold leading-[18px] text-title lg_text-base lg_leading-5'>
          Maximum Redemption Of 500 Points Per Order.
        </p>
        <div className='mt-[30px] flex flex-col items-center justify-center sm_flex-row lg_mt-[50px]'>
          <button className=' h-[60px] w-[200px] rounded-[10px] bg-primary  text-sm font-normal text-white hover_opacity-90 active_scale-[.97] lg_text-base'>
            <Link
              className='flex h-full w-full items-center justify-center font-poppins font-[600] '
              href='/checkout'
            >
              GO CHECKOUT
            </Link>
          </button>
          <button className='mt-5 h-[60px] w-[200px] rounded-[10px] border-2 border-solid border-primary bg-white text-sm font-normal text-primary hover_opacity-90 active_scale-[.97] sm_ml-[140px] sm_mt-0 sm_border-none sm_bg-primary sm_text-white lg_text-base'>
            <Link
              href='/bird-feeders'
              className='flex h-full w-full items-center justify-center font-poppins font-[600] '
            >
              GO SHOPPING
            </Link>
          </button>
        </div>
      </div>

      <div className='mt-[30px] text-center text-sm font-normal leading-5 text-primary lg_mt-[50px] lg_text-lg lg_leading-[22px]'>
        REDEEM COUPONS
      </div>

      <div
        className={clsx(
          'mx-auto mt-[20px] max-w-[1440px]  gap-x-[15px] gap-y-[15px] lg_mt-[50px] lg_gap-x-[35px]  ',
          'grid grid-cols-[repeat(2,170px)] justify-center justify-items-center sm_grid-cols-[repeat(3,170px)] lg_grid-cols-[repeat(3,260px)] min-[1200px]_grid-cols-5',
        )}
      >
        {redeemCouponConfigs.map((x, i) => {
          return (
            <div
              key={i}
              className={clsx(
                'relative h-[180px] w-full max-w-[170px] overflow-hidden lg_h-[300px]  lg_max-w-[260px]  ',
                'rounded-[10px] bg-[#F6F6F6]',
                isSignedIn ? '' : 'group lg_hover_border-none',
              )}
              onClick={() => {
                handleMobileCardClick(x);
              }}
            >
              <Image
                alt='point'
                src={couponPointPng}
                width={42}
                height={40}
                className={clsx(
                  'mx-auto mt-[25px] w-[30px]  lg_w-[42px]',
                  isSignedIn ? 'lg_mt-[30px]' : 'lg_mt-[77px]',
                )}
              />
              <div className='mt-2.5 text-center text-base font-normal leading-5 text-primary lg_mt-[15px] lg_text-lg lg_leading-[22px]'>
                {x.title}
              </div>
              <p
                className={clsx(
                  'absolute bottom-[47px]  w-full',
                  'text-center font-sans text-sm font-medium  leading-[18px] text-title  lg_text-base lg_font-[700] lg_leading-5',
                  isSignedIn ? 'lg_bottom-[163px]' : 'lg_bottom-[115px]',
                )}
              >
                {x.subTitle}
              </p>
              <p
                className={clsx(
                  'absolute bottom-[17px] w-full ',
                  'text-center  font-sans text-base font-bold leading-5 text-primary  lg_text-[18px] lg_font-[600]',
                  isSignedIn ? 'lg_bottom-[125px]' : 'lg_bottom-[77px]',
                )}
              >
                {x.pointDesc}
              </p>

              {isSignedIn ? (
                <button
                  className={clsx(
                    'absolute bottom-[30px] left-0 right-0 mx-7 hidden py-5 lg_block',
                    'text-center text-base font-normal leading-5 text-primary',
                    'bg-white active_scale-[.97] disabled_scale-100',
                    'hover_bg-primary hover_text-white',
                  )}
                  onClick={e => {
                    e.stopPropagation();
                    x.btnOnClick();
                  }}
                  disabled={x.loading}
                >
                  {x.loading ? 'REDEEM NOW...' : 'REDEEM NOW'}
                </button>
              ) : null}

              {isSignedIn ? null : <SignTip className={clsx('group-hover_lg_block')} />}
            </div>
          );
        })}
      </div>

      <RedeemModal
        open={showConfirmModal}
        onOk={() => {
          setShowConfirmModal(false);
          confirmResolveRef.current(true);
        }}
        onCancel={() => {
          setShowConfirmModal(false);
          confirmResolveRef.current(false);
        }}
        title='ARE YOU SURE TO REDEEM THE POINTS FOR THE COUPON?'
        description='This Action Cannot Be Undone. Click "Confirm" To Redeem Or "Cancel" To Go Back.'
        confirmText='CONFIRM'
        cancelText='CANCEL'
      />
      <RedeemModal
        open={redeemModalInfo.open}
        onCancel={() => {
          setRedeemModalInfo(initRedeemInfoState);
        }}
        onOk={() => {
          if (redeemModalInfo.type === 'succeed') {
            router.push('/bird-feeders');
          }

          setRedeemModalInfo(initRedeemInfoState);
        }}
        type={redeemModalInfo.type}
        title={
          redeemModalInfo.type === 'succeed' ? (
            <div className='flex flex-col items-center justify-center lg_flex-row'>
              <span>REDEEMED! THE CODE IS:&nbsp;</span>
              <span className='flex items-center'>
                {couponCode}
                <Image
                  src={copyPng}
                  width={18}
                  height={18}
                  alt='copy'
                  className='ml-2.5 cursor-pointer'
                  onClick={() => {
                    handleCopy(couponCode);
                  }}
                />
              </span>
            </div>
          ) : (
            'REDEMPTION FAILED!'
          )
        }
        description={
          redeemModalInfo.type === 'succeed' ? (
            <>
              <div>You Have Successfully Redeemed The Points For the Coupon,</div>
              <div>Apply It When Checkout!</div>
            </>
          ) : (
            redeemModalInfo.description
          )
        }
        confirmText={redeemModalInfo.type === 'succeed' ? 'SHOP NOW' : 'CONFIRM'}
      />
    </div>
  );
};

export default RedeemPoints;
